import { FC, Fragment } from "react";

import HomeSectionCard from "src/components/HomeSectionCard/HomeSectionCard";
import {
	collectHomeCards,
	navigationConfig,
	type HomeCard
} from "src/components/layout/navConfig";
import UserAdminFilter from "src/containers/UserAdminFilter/UserAdminFilter";
import useAuth from "src/hooks/useAuth/useAuth";
import { getTimeOfDayGreeting } from "./getTimeOfDayGreeting";

/**
 * `navigationConfig` is a module-level constant and `collectHomeCards`
 * is pure, so the derived tile list never changes at runtime. Compute
 * it once at module load instead of rebuilding it on every render of
 * `Home` (which would happen on every auth / permission update).
 */
const homeCards = collectHomeCards(navigationConfig);

/**
 * Admin home. Tiles and their ordering are derived from `navConfig.ts`
 * via {@link collectHomeCards}, so the sidebar and the home page can
 * never drift out of sync — annotating a nav item with a `home` block
 * makes it appear here, and removing it removes it here.
 */
const Home: FC = () => {
	const { user } = useAuth();

	if (!user) return null;

	return (
		<div
			className="mx-auto w-full 2xl:max-w-[1440px]"
			data-testid="appTemplate-home"
		>
			<h1 className="text-3xl xl:text-4xl font-bold mb-10">
				{getTimeOfDayGreeting()} {user.firstname}!
			</h1>

			<div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
				{homeCards.map(renderCard)}
			</div>
		</div>
	);
};

const renderCard = (card: HomeCard) => {
	if (card.openLink) {
		const cardElement = (
			<HomeSectionCard
				title={card.label}
				description={card.description}
				icon={card.icon}
				openLink={card.openLink}
			/>
		);

		if (card.sectionPermission) {
			return (
				<UserAdminFilter
					key={card.key}
					requiredPermission={card.sectionPermission}
				>
					{cardElement}
				</UserAdminFilter>
			);
		}

		return <Fragment key={card.key}>{cardElement}</Fragment>;
	}

	return (
		<HomeSectionCard
			key={card.key}
			title={card.label}
			description={card.description}
			icon={card.icon}
			subLinks={card.subLinks}
			subLinksTwoColumn={card.twoColumn}
		/>
	);
};

export default Home;
